import { createFileRoute } from "@tanstack/react-router";
import ContactForm from "@/components/Contact/Form";
import CTA from "@/components/LandingPage/CTA";
import { HugeiconsIcon } from "@hugeicons/react";
import Mail01Icon from "@hugeicons/core-free-icons/Mail01Icon";
import Message01Icon from "@hugeicons/core-free-icons/Message01Icon";
import Clock01Icon from "@hugeicons/core-free-icons/Clock01Icon";
import Heading from "@/components/layout/Heading";
import {
  createLLMOMeta,
  createWebPageSchema,
  createBreadcrumbSchema,
} from "@/lib/seo";
import { useTranslation } from "@/lib/i18n";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: "Contact Us - Zapify | Get Support & Answers",
        description:
          "Get in touch with the Zapify team for support, questions, or feedback. We're here to help you succeed with AI-powered Instagram automation.",
        url: "https://zapify.pro/contact",
      }),
      {
        property: "og:image",
        content: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png",
      },
      { property: "og:locale", content: "en_US" },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/contact" }],
    scripts: [
      createWebPageSchema({
        name: "Contact Us - Zapify",
        description:
          "Get in touch with the Zapify team for support, questions, or feedback.",
        url: "https://zapify.pro/contact",
      }),
      createBreadcrumbSchema({
        itemListElement: [
          {
            position: 1,
            name: "Home",
            item: "https://zapify.pro",
          },
          {
            position: 2,
            name: "Contact",
            item: "https://zapify.pro/contact",
          },
        ],
      }),
      {
        type: "application/ld+json" as const,
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          mainEntity: {
            "@type": "Organization",
            name: "Zapify",
            url: "https://zapify.pro",
            contactPoint: {
              "@type": "ContactPoint",
              email: "[email protected]",
              contactType: "Customer Support",
            },
          },
        }),
      },
    ],
  }),
  component: ContactPage,
});

const socialLinks = [
  { label: "Instagram", username: "@zapify.pro", href: "https://www.instagram.com/Zapify.pro", src: "/social/instagram_-con.webp" },
  { label: "TikTok", username: "@zapifypro", href: "https://www.tiktok.com/@zapifypro", src: "/social/tiktok-icon.webp" },
  { label: "YouTube", username: "@ZapifyPro", href: "https://www.youtube.com/@ZapifyPro", src: "/social/youtube-icon.png" },
  { label: "Facebook", username: "@ZapifyPro", href: "https://www.facebook.com/ZapifyPro", src: "/social/facebook-icon.png" },
  { label: "Threads", username: "@zapify.pro", href: "https://www.threads.com/@zapify.pro", src: "/social/thread-icon.png" },
  { label: "X", username: "@ZapifyPro", href: "https://x.com/ZapifyPro", src: "/social/x-icon.png" },
  { label: "LinkedIn", username: "@zapifypro", href: "https://www.linkedin.com/company/zapifypro/", src: "/social/linkedin-icon.png" },
  { label: "Reddit", username: "@ZapifyPro", href: "https://www.reddit.com/r/ZapifyPro", src: "/social/reddit-icon.png" },
];

function ContactPage() {
  const { translations: t } = useTranslation();

  return (
    <div className="min-h-screen relative overflow-hidden bg-white dark:bg-black transition-colors duration-300">
      {/* Radial fade for background */}
      <div className="absolute inset-0 bg-white/50 dark:bg-black/20 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none"></div>
      <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute -top-20 -right-32 w-80 h-80 bg-gradient-to-r from-purple-400/15 to-pink-400/15 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/3 -left-40 w-96 h-96 bg-gradient-to-r from-blue-400/10 to-cyan-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 right-20 w-64 h-64 bg-gradient-to-r from-yellow-400/12 to-orange-400/12 rounded-full blur-2xl animate-pulse delay-2000"></div>
        <div className="absolute top-1/2 left-1/4 w-48 h-48 bg-gradient-to-r from-green-400/8 to-emerald-400/8 rounded-full blur-3xl animate-pulse delay-500"></div>
        <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-gradient-to-r from-indigo-400/10 to-purple-400/10 rounded-full blur-3xl animate-pulse delay-1500"></div>
      </div>
      <div className="relative z-10 pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <Heading
          title={<>Contact <span className="gradient-text">Support</span></>}
          description={t.contactPage.subtitle}
          showLine={false}
          className="mb-12 sm:mb-16 mt-4 sm:mt-8"
        />

        <div className="grid lg:grid-cols-3 gap-8 items-start">
          {/* Left Column: Connect with Us Sidebar */}
          <div className="lg:col-span-1 space-y-6 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center">
                <HugeiconsIcon icon={Message01Icon} size={16} color="currentColor" className="text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h4 className="font-bold text-neutral-900 dark:text-white text-base">
                  {t.contactPage.connectWithUs}
                </h4>
                <p className="text-neutral-600 dark:text-neutral-400 text-xs">
                  {t.contactPage.connectDescription}
                </p>
              </div>
            </div>

            {/* Email Us */}
            <div className="border border-dashed border-blue-300/50 dark:border-blue-700/50 rounded-xl p-5 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all duration-300 cursor-pointer group">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <HugeiconsIcon icon={Mail01Icon} size={20} color="currentColor" className="text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <h5 className="font-semibold text-neutral-900 dark:text-white text-sm mb-1">
                    {t.contactPage.emailUs}
                  </h5>
                  <p className="text-neutral-600 dark:text-neutral-400 text-xs mb-2">
                    {t.contactPage.emailResponse}
                  </p>
                  <a
                    href="mailto:support@zapify.pro"
                    className="text-blue-600 dark:text-blue-400 font-medium hover:underline transition-all duration-200 text-sm"
                  >
                    support@zapify.pro
                  </a>
                </div>
              </div>
            </div>

            {/* Live Chat */}
            <div className="border border-dashed border-green-300/50 dark:border-green-700/50 rounded-xl p-5 hover:bg-green-50/50 dark:hover:bg-green-900/10 transition-all duration-300 cursor-pointer group">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-green-50 dark:bg-green-900/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <HugeiconsIcon icon={Message01Icon} size={20} color="currentColor" className="text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1">
                  <h5 className="font-semibold text-neutral-900 dark:text-white text-sm mb-1">
                    {t.contactPage.support}
                  </h5>
                  <p className="text-neutral-600 dark:text-neutral-400 text-xs mb-3">
                    {t.contactPage.getInstantHelp}
                  </p>
                  <div className="flex items-center gap-3">
                    <a
                      href="https://m.me/zapifypro"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-600 hover:bg-blue-700 transition-all duration-200 hover:scale-110"
                      aria-label="Messenger"
                    >
                      <img
                        src="https://cdn.simpleicons.org/messenger/white"
                        alt="Messenger icon"
                        className="w-4 h-4 brightness-200"
                      />
                    </a>
                    <a
                      href="https://www.instagram.com/ZapifyPro"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-8 h-8 rounded-lg bg-pink-600 hover:bg-pink-700 transition-all duration-200 hover:scale-110"
                      aria-label="Instagram"
                    >
                      <img
                        src="https://cdn.simpleicons.org/instagram/white"
                        alt="Instagram icon"
                        className="w-4 h-4 brightness-200"
                      />
                    </a>
                    <a
                      href="https://calendly.com/saurabhdhar/zapify"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-auto h-8 px-3 gap-2 rounded-lg bg-white dark:bg-black border border-neutral-200 dark:border-neutral-700 hover:border-blue-500 dark:hover:border-blue-500 transition-all duration-200 hover:scale-105"
                      aria-label={t.contactPage.scheduleMeet}
                    >
                      <img
                        src="https://outfunnel.com/wp-content/uploads/2021/10/calendly-icon.png"
                        alt="Calendly icon"
                        className="w-4 h-4 object-contain"
                      />
                      <span className="text-[10px] font-semibold text-neutral-900 dark:text-white">
                        {t.contactPage.scheduleMeet}
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Follow Us */}
            <div className="border border-dashed border-neutral-300/50 dark:border-neutral-700/50 rounded-xl p-5 hover:bg-neutral-50/50 dark:hover:bg-neutral-900/50 transition-all duration-300">
              <h5 className="font-semibold text-neutral-900 dark:text-white text-sm mb-2">
                <span className="gradient-text">{t.contactPage.followUs}</span>
              </h5>
              <p className="text-neutral-600 dark:text-neutral-400 text-xs mb-4">
                {t.contactPage.stayUpdated}
              </p>
              <div className="flex flex-nowrap gap-2">
                {socialLinks.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={item.label}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white ring-1 ring-black/8 transition-all duration-200 hover:scale-110 hover:shadow-sm dark:bg-zinc-900 dark:ring-white/15"
                    title={item.label}
                  >
                    <img
                      src={item.src}
                      alt=""
                      width={20}
                      height={20}
                      className="h-5 w-5 rounded-full object-contain object-center"
                      loading="lazy"
                    />
                  </a>
                ))}
              </div>
            </div>

            {/* Response Time Badge */}
            <div className="flex justify-center pt-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-dashed border-green-300/50 dark:border-green-700/50 rounded-full bg-green-50/50 dark:bg-green-900/10">
                <HugeiconsIcon icon={Clock01Icon} size={12} color="currentColor" className="text-green-600 dark:text-green-400" />
                <span className="text-xs font-medium text-green-700 dark:text-green-400">
                  {t.contactPage.responseTime}
                </span>
              </div>
            </div>
          </div>

          {/* Right Column: Contact Form covering 2 cols */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 overflow-hidden shadow-sm shadow-lg h-full">
              <ContactForm embedded />
            </div>
          </div>
        </div>

        {/* Contact CTA - moved outside and below the grid */}
        <div className="mt-16">
          <CTA />
        </div>
      </div>
    </div>
  );
}
